import {Injectable, OnDestroy} from '@angular/core';
import {
  VideoTutorial,
  videoTutorialConfig,
} from '@constants/video-tutorial.config';
import {UserStore} from '@stores/user.store';
import {Observable, ReplaySubject, Subject, merge} from 'rxjs';
import {RouteStore} from '@stores/route.store';
import {distinctUntilChanged, map, take, takeUntil, tap} from 'rxjs/operators';
import {UtilsPL2 as U} from '@common/utils/dist/utils/utils-pl2.js';
import {VideoTutorialUtils as VTU} from '@utils/video-tutorial-utils';
import {ConnectionStore} from '@stores/connection.store';
import {StoreEventsPipes as SEP} from '@utils/store-events-pipes';
import {AppDecisionsBuilder} from '@features/app-decisions-builder';

@Injectable({
  providedIn: 'root',
})
export class VideoTutorialObserverService implements OnDestroy {
  private _destroyed$ = new ReplaySubject<boolean>();
  private _resetAll$ = new Subject<void>();

  private _video$: Observable<VideoTutorial | undefined>;

  private _currentTutorial: VideoTutorial | undefined;

  private _nextVideosQueue: VideoTutorial[] = [];

  constructor(
    private userStore: UserStore,
    private routeStore: RouteStore,
    private connectionStore: ConnectionStore,
    private appDecisionsBuilder: AppDecisionsBuilder,
  ) {
    this._video$ = merge(
      this.userStore.state$.pipe(SEP.userStoreEvents(), VTU.namedValue('user')),
      this.routeStore.state$.pipe(
        SEP.routeStoreEvents(),
        VTU.namedValue('route'),
      ),
      this.connectionStore.state$.pipe(
        SEP.connectionStoreEvents(),
        VTU.namedValue('device'),
      ),
      this._resetAll$.pipe(VTU.namedValue('resetAll')),
    ).pipe(
      takeUntil(this._destroyed$),
      map((trigger) => {
        const vtuTrigger = trigger as VTU.Trigger;
        const shouldDeactivate = VTU.shouldDeactivate(
          this._currentTutorial?.id,
          vtuTrigger,
          {
            device: this.connectionStore.state(),
            route: this.routeStore.state(),
            user: this.userStore.state(),
          },
        );

        const previousVideo: VideoTutorial | undefined = shouldDeactivate
          ? undefined
          : this._currentTutorial;

        const newVideo = VTU.findCorrectTutorials(vtuTrigger, {
          device: this.connectionStore.state(),
          route: this.routeStore.state(),
          user: this.userStore.state(),
          feature: this.appDecisionsBuilder.buildDecisions(
            this.userStore.state(),
          ),
        });

        if (newVideo) {
          this._nextVideosQueue.push(...newVideo);
        }

        return previousVideo ?? this._nextVideosQueue.shift();
      }),
      tap((t) => (this._currentTutorial = t)),
      distinctUntilChanged(),
    );
  }

  video$() {
    return this._video$;
  }

  ngOnDestroy() {
    this._destroyed$.next(true);
    this._destroyed$.complete();
  }

  close() {
    this.userStore
      .updateUser({
        tutorial: {
          ...this.userStore.state().tutorial,
          [this._currentTutorial?.id]: 1,
        },
      })
      .pipe(take(1))
      .subscribe();
  }

  resetAll() {
    this.userStore
      .updateUser({
        tutorial: U.exclude(
          this.userStore.state().tutorial,
          ...videoTutorialConfig.map((t) => t.id),
        ),
      })
      .pipe(take(1))
      .subscribe(() => {
        this._resetAll$.next();
      });
  }
}
